<template>
  <div class="is-flex is-flex-wrap-wrap">
    <div class="box">
      <details-box-heading label="Comments" class="mb-3" />

      <div class="mb-1 cards">
        <div v-if="actions.length > 0">
          <div
            v-for="action in actions"
            :key="action.id"
            class="card mb-3 is-clickable"
            :class="{
              inbound: action.is_inbound,
              outbound: !action.is_inbound,
            }"
            @click="() => onSelectAction(action)"
          >
            <div class="comment-icons">
              <i v-if="action === selectedAction" class="fas fa-star marked-comment"></i>
              <i
                v-if="isWithinOneDay(action.created_at)"
                class="fas fa-trash delete-comment"
                @click.stop="deleteComment(action)"
              ></i>
            </div>
            <div class="card-content">
              <p style="font-size: 1.1em">{{ action.value }}</p>
              <p>
                <span
                  v-if="isSalesTask"
                  class="context-badge"
                  :class="getContextClass(action.context)"
                  >{{ action.context }}</span
                >
                <!-- EH-1840: this comment was not typed in ERP but taken over
                     from a HubSpot deal. Without the distinction it looks like
                     it came from a colleague here. -->
                <span v-if="action.source === 'hubspot'" class="context-badge source-hubspot"
                  >HubSpot</span
                >
                {{ action.created_by }}, {{ formatDate(new Date(action.created_at)) }}
                <!-- Only filled when the note was edited in HubSpot after we
                     took it over and we updated the text here. Silently
                     rewritten history is not what anyone expects. -->
                <span v-if="action.updated_at" class="edited-note"
                  >, bewerkt {{ formatDate(new Date(action.updated_at)) }}</span
                >
              </p>
            </div>
          </div>
        </div>
        <div v-else>
          No {{ isSalesTask ? 'comments' : 'Internal comments' }} for this address yet.
        </div>
      </div>

      <!-- Predefined comment templates for sales tasks -->
      <div v-if="isSalesTask" class="mb-2">
        <span class="label is-small">Comments:</span>
        <div class="buttons are-small">
          <button
            v-for="template in commentTemplates"
            :key="template.id"
            class="button is-light"
            :class="{ 'is-info': action.value === template.text }"
            @click="selectCommentTemplate(template.text)"
          >
            {{ template.label }}
          </button>
          <button
            class="button is-light"
            :class="{ 'is-info': isCustomComment }"
            @click="selectCustomComment"
          >
            Aangepast
          </button>
        </div>
      </div>

      <textarea
        class="textarea"
        :placeholder="
          isSalesTask
            ? 'Selecteer een opmerking hierboven of typ een aangepaste opmerking...'
            : 'Opmerking...'
        "
        rows="1"
        :disabled="action.isLoading"
        v-model="action.value"
        :class="{
          'is-success': result.type === 'Success',
          'is-danger': result.type === 'Error',
        }"
        @keypress="onKeyPress"
        @input="onCommentInput"
      ></textarea>
      <div style="display: flex; justify-content: space-between; margin-top: 0.8em">
        <p
          class="pl-1"
          :class="{
            success: result.type === 'Success',
            error: result.type === 'Error',
          }"
        >
          {{ result.message }}
        </p>
        <div style="display: flex; align-items: flex-end; gap: 0.5rem">
          <div v-if="isSalesTask" style="display: flex; flex-direction: column">
            <span style="font-size: 0.8em; margin-bottom: 0.2em">Context</span>
            <dropdown
              display="label"
              class="context-dropdown"
              :preselect="action.context"
              :items="contextDropdown"
              @change="(item) => item.handler()"
            />
          </div>
          <label class="mr-2 mb-3 checkbox" style="display: flex; align-items: flex-end">
            <span class="pr-1" style="font-size: 1.1em">Notify</span>
            <input
              type="checkbox"
              @change="() => (action.has_follow_up = !action.has_follow_up)"
              :checked="action.has_follow_up"
            />
          </label>
          <button
            :disabled="!action.value"
            :class="{ 'is-loading': action.isLoading }"
            class="button is-info is-small"
            @click="create"
          >
            Log
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Action, { ExistingAction } from '@/logic/CallCentreScreen/CallCentreAction'
import { actions } from '@/store'
import { Component, Prop, Vue } from 'vue-property-decorator'
import { STATUS_ERROR, STATUS_OK } from '@/constants'
import request from '@/requests'
import { notifications } from '@/util'
import DetailsPaneBase from './DetailsPaneBase.vue'

@Component({
  name: 'DetailsCallCentreTextBox',
})
export default class DetailsCallCentreTextBox extends DetailsPaneBase {
  protected action = new Action()
  protected actions: Array<Action> = []
  protected result = {
    type: '',
    message: '',
  }

  protected selectedAction: Action | null = null
  protected isCustomComment = false

  get isSalesTask() {
    return this.type === 'sales'
  }

  get commentTemplates() {
    return [
      { id: 1, label: 'Interesse in Boilers', text: 'Klant geïnteresseerd in boilers' },
      { id: 2, label: 'Interesse in Warmtepomp', text: 'Klant geïnteresseerd in warmtepomp' },
      { id: 3, label: 'Interesse in Batterij', text: 'Klant geïnteresseerd in batterij' },
    ]
  }

  get contextDropdown() {
    const contexts = this.action.possibleContext
    const dropdown = contexts.map((value) => ({
      label: value,
      handler: () => {
        this.action.context = value
      },
    }))
    return dropdown
  }

  protected getContextClass(context: string) {
    switch (context) {
      case 'D2D':
        return 'context-d2d'
      case 'Phone':
        return 'context-phone'
      case 'Email':
        return 'context-email'
      case 'Internal':
      default:
        return 'context-internal'
    }
  }

  protected selectCommentTemplate(text: string) {
    this.action.value = text
    this.isCustomComment = false
  }

  protected selectCustomComment() {
    this.action.value = ''
    this.isCustomComment = true
  }

  protected onCommentInput() {
    // Check if the current value matches any template
    const matchesTemplate = this.commentTemplates.some(
      (template) => template.text === this.action.value
    )
    this.isCustomComment = !matchesTemplate && this.action.value.length > 0
  }

  protected onKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey && this.action.value.trim().length > 0) {
      this.create()
    }
  }

  protected async onSelectAction(action: Action) {
    if (action === this.selectedAction) return

    const response = await request.details.updateTaskDataField(this.address, this.type, {
      comment_id: action.id,
    })
    if (response.status !== STATUS_ERROR) {
      this.setSelectedAction(action)
      this.updateMasterData()
    }
    if (response.status !== STATUS_OK) {
      notifications.addNotification({
        message: response.message,
        type: response.status === STATUS_ERROR ? 'danger' : 'warning',
      })
    }
  }

  protected formatDate = (date: Date) => {
    let day = String(date.getDate())
    let month = String(date.getMonth() + 1)
    const year = date.getFullYear()

    let hour = String(date.getHours())
    let minute = String(date.getMinutes())

    if (Number(hour) < 10) hour = `0${hour}`
    if (Number(minute) < 10) minute = `0${minute}`
    if (Number(day) < 10) day = `0${day}`
    if (Number(month) < 10) month = `0${month}`

    return `${day}/${month}/${year} - ${hour}:${minute}`
  }

  protected isWithinOneDay(createdAt: string): boolean {
    const createdDate = new Date(createdAt)
    const now = new Date()
    const oneDayMs = 24 * 60 * 60 * 1000
    return now.getTime() - createdDate.getTime() < oneDayMs
  }

  protected async deleteComment(action: Action) {
    if (!action.id) return

    const response = await request.actions.deleteAction(action.id)

    if (response.status !== STATUS_OK) {
      notifications.addNotification({
        message: response.message || 'Failed to delete comment',
        type: 'danger',
      })
      return
    }

    this.actions = this.actions.filter((a) => a.id !== action.id)

    if (this.selectedAction === action) {
      this.selectedAction = null
    }

    notifications.addNotification({
      message: 'Comment deleted successfully',
      type: 'success',
    })
  }

  protected setSelectedAction(action?: Action) {
    if (action) {
      this.selectedAction = action
    }
  }

  protected subject() {
    switch (this.type) {
      case 'installation':
        return 'Installatie'
      case 'sales':
        return 'Werving'
      case 'design':
        return 'Algemeen/proces'
      case 'inspection':
        return 'Schouwing'
      default:
        return 'installatie'
    }
  }

  protected async create() {
    this.action.address = this.address
    this.action.type = 'Comment'
    this.action.woco = this.manager.selectedWoco || ''
    // Use selected context for sales tasks, 'Internal' for others
    if (!this.isSalesTask) {
      this.action.context = 'Internal'
    }
    this.action.created_by = actions.auth.readUser()
    this.action.subject = this.subject()
    this.action.is_inbound = true
    this.action.value = this.action.value.trim()
    const response = await this.action.create()

    this.result.message = response.message
    this.result.type = response.status === 'OK' ? 'Success' : 'Error'

    setTimeout(() => {
      this.result = {
        type: '',
        message: '',
      }
    }, 3000)

    if (response.status === 'OK') {
      this.onSelectAction(this.action)
      this.actions.unshift(this.action)
      this.action = new Action()
    }
  }

  protected async getActions() {
    const response = await request.actions.getActionsForAddress(this.address)

    if (response.data && response.data.actions) {
      return response.data.actions
        .map((action: ExistingAction) => new Action(action))
        .filter((action: ExistingAction) => {
          const matchesSubject = action.subject === this.subject()
          // For sales tasks, show all contexts; for others, only Internal
          const matchesContext = this.isSalesTask || action.context === 'Internal'
          return matchesSubject && matchesContext
        })
    }

    return []
  }

  async mounted() {
    this.actions = await this.getActions()
    const taskData = this.selectedMasterItem.getTaskExtraData(this.type)
    if (!taskData || !taskData.comment_id || this.actions.length < 1) {
      return
    }
    const selectedAction = this.actions.find(
      (action) => action.id.toString() === taskData.comment_id
    )
    if (selectedAction) {
      this.selectedAction = selectedAction
    }
  }
}
</script>

<style scoped lang="scss">
.success {
  color: limegreen;
}

.error {
  color: red;
}

.textarea {
  font-size: 0.75rem;
  padding: 0.25rem;
}

.cards {
  min-height: 2.5em;
  max-height: 15em;
  overflow-y: auto;
  flex-grow: 1;
  padding: 0.5em;

  &::-webkit-scrollbar {
    width: 0.35rem;
    height: 0.35rem;
    color: #868686;
  }

  &::-webkit-scrollbar-track {
    -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
    box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.5);
    box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.5);
    border-radius: 10px;
    cursor: grab;
  }
}

.card {
  .card-content {
    padding: 0.8rem !important;

    .content {
      display: flex;
      flex-direction: column;
    }
  }

  .comment-icons {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    display: flex;
    gap: 0.5rem;
  }

  .marked-comment {
    color: #c7d6de;
    font-size: 0.75rem;
  }

  .delete-comment {
    color: #c7d6de;
    font-size: 0.75rem;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
      color: #e74c3c;
    }
  }
}

.inbound {
  background-color: #edf6fa;
}

.outbound {
  background-color: #eaf7e1;
}

.context-badge {
  display: inline-block;
  padding: 0.2rem 0.4rem;
  border-radius: 0.25rem;
  font-size: 0.7rem;
  font-weight: 600;
  margin-right: 0.5rem;
  text-transform: uppercase;
}

.context-phone {
  background-color: #3498db;
  color: white;
}

.context-email {
  background-color: #e74c3c;
  color: white;
}

.context-d2d {
  background-color: #f39c12;
  color: white;
}

.source-hubspot {
  background-color: #ff7a59;
  color: white;
}

.edited-note {
  font-style: italic;
  opacity: 0.8;
}

.context-internal {
  background-color: #95a5a6;
  color: white;
}

.buttons.are-small {
  margin-bottom: 0.5rem;

  .button {
    margin-right: 0.25rem;
    margin-bottom: 0.25rem;
    font-size: 0.7rem;
    padding: 0.25rem 0.5rem;

    &.is-info {
      background-color: #3273dc;
      border-color: #3273dc;
      color: white;
    }
  }
}
</style>
